import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum StlPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

// Used to create a summary of items grouped by item rather than SKU
export enum StlPurchaseOrderQuantitySummaryLineItemKeys {
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  itemQty = 'itemQty', // quantity of the item for order
  unitOfMeasure = 'unitOfMeasure', // unit of measure for the item (i.e. jars or pallets)
}

export const stlPurchaseOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  vendor: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  quantitySummary: yup
    .array()
    .of(
      yup.object().shape({
        itemName: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
})

export type StlPurchaseOrderFileData = yup.InferType<typeof stlPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<StlPurchaseOrderFileData>

export const stlPurchaseOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: '70939311-f6a8-4085-b3c0-23c40df22d6e',
    companyName: '4dca4bd7-60eb-477c-a3a0-0bd3416844db',
    address: 'ecbc195b-3b67-4543-9ba0-1f1aec5fe2b6',
    phoneNumber: '0f5d8dae-81c5-47cb-9b3c-f53bd4f9064e',
    emailAddress: '0789a2e3-d1cf-49e2-9fb7-c36f8d223edc',
  },
  orderNumber: 'a80d28f6-e947-4210-a085-0dcb89041e7a',
  paymentTerms: '6802ca2b-01d9-4691-a47d-a213a795f896',
  freightTerms: '4e4ed81e-3993-4c66-96ce-0e3d62086565',
  dateIssued: 'adc5e639-b6ef-49c8-b6f8-300f16393e3d',
  dateReady: '62065d39-9d43-4944-a571-76119809bfd7',
  destination: {
    attn: 'f67de120-1273-4a08-9389-7cf49fb17e61',
    companyName: 'e7698684-c3da-41ec-9b3b-e6f9ac389411',
    address: 'c0607bfa-a092-4fe7-8ace-914087557753',
    phoneNumber: 'c03dbeee-16eb-474a-b5c5-37886ad92f91',
    emailAddress: '9971fe18-1035-4afd-91e3-9299d9cd0e06',
  },
  vendor: {
    attn: '66ba90f1-5c59-4891-b90c-fa3dc16150ff',
    companyName: '33fe0881-c782-4d94-a17e-5ac60ba34ca8',
    address: '9b474c1d-4a66-4310-852f-73dab69f08a8',
    phoneNumber: '69235d0f-f050-4e37-b7f7-2f5f67056e0f',
    emailAddress: 'da5d5979-4050-4df8-bdf2-347e18ef64a4',
  },
  items: [
    {
      itemNumber: null,
      itemName: '31cf6fdf-5713-4e37-b2b3-9ba24e9a9d8a',
      description: 'a6b40d66-ab97-42aa-b29d-81751ae15e50',
      itemQty: '8e5b9b82-ca28-4f3e-a26d-2cd272372736',
      unitPrice: '5355aee1-773c-4980-94be-ef69b4a6a41c',
      lineTotal: null,
    },
  ],
  quantitySummary: [
    {
      itemName: '634ed75c-b3b9-4a15-87e1-b83d198fff12',
      itemQty: '86f02a03-0762-405e-9dd0-2bfdb715262f',
      unitOfMeasure: '3fe52b1b-c188-4dd9-8c5f-407451dd6b46',
    },
  ],
  agreement: 'f87a675c-a075-4829-b6c4-f1c1f6b72159',
  instructions: '76c64adb-8ef5-491f-9b7c-e9ed0b5ece29',
}
